/**
 * API Service for Customer Management System
 * Handles all backend API communications
 */

// API base URL - should match your backend configuration
const API_BASE_URL = 'https://kit.sainisolutions.in/backend/api';

// Debug logging
console.log('API Base URL:', API_BASE_URL);

class APIService {
    constructor() {
        this.baseURL = API_BASE_URL;
        this.token = localStorage.getItem('auth_token');
    }

    // Generic API request method
    async request(endpoint, options = {}) {
        const url = `${this.baseURL}/${endpoint}`;
        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            ...options,
        };

        // Add authorization header if token exists
        if (this.token) {
            config.headers.Authorization = `Bearer ${this.token}`;
        }

        try {
            console.log(`API Request: ${url}`, { method: options.method || 'GET' });
            const response = await fetch(url, config);
            
            // Check if response is JSON
            const contentType = response.headers.get('content-type');
            let data;
            
            if (contentType && contentType.includes('application/json')) {
                data = await response.json();
            } else {
                const text = await response.text();
                throw new Error(`Unexpected response format: ${text}`);
            }
            
            if (!response.ok) {
                console.error('API Error Response:', data);
                throw new Error(data.message || `API request failed with status ${response.status}`);
            }
            
            return data;
        } catch (error) {
            console.error('API Request Error:', {
                url,
                error: error.message,
                status: error.response?.status,
                config
            });
            
            // Provide more user-friendly error messages
            if (error.message.includes('Failed to fetch')) {
                throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
            }
            
            throw error;
        }
    }

    // Authentication methods
    async login(username, password) {
        const response = await this.request('auth.php', {
            method: 'POST',
            body: JSON.stringify({
                action: 'login',
                username,
                password
            })
        });

        if (response.status === 'success') {
            this.token = response.data.token;
            localStorage.setItem('auth_token', this.token);
            localStorage.setItem('user_data', JSON.stringify(response.data.user));
        }

        return response;
    }

    logout() {
        this.token = null;
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
    }

    // Customer API methods
    async getCustomers() {
        return await this.request('customers.php');
    }

    async getCustomer(id) {
        return await this.request(`customers.php/${id}`);
    }

    async createCustomer(customerData) {
        return await this.request('customers.php', {
            method: 'POST',
            body: JSON.stringify(customerData)
        });
    }

    async updateCustomer(id, customerData) {
        return await this.request(`customers.php/${id}`, {
            method: 'PUT',
            body: JSON.stringify(customerData)
        });
    }

    async deleteCustomer(id) {
        return await this.request(`customers.php/${id}`, {
            method: 'DELETE'
        });
    }

    async searchCustomers(searchTerm) {
        return await this.request(`customers.php?search=${encodeURIComponent(searchTerm)}`);
    }

    // Transaction API methods
    async getTransactions(limit = null, offset = 0) {
        let url = 'transactions.php';
        const params = new URLSearchParams();
        
        if (limit) params.append('limit', limit);
        if (offset) params.append('offset', offset);
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
        
        return await this.request(url);
    }

    async getTransactionStats(startDate = null, endDate = null) {
        let url = 'transactions.php?stats=1';
        
        if (startDate) url += `&start_date=${startDate}`;
        if (endDate) url += `&end_date=${endDate}`;
        
        return await this.request(url);
    }

    async createTransaction(transactionData) {
        return await this.request('transactions.php', {
            method: 'POST',
            body: JSON.stringify(transactionData)
        });
    }

    async updateTransaction(id, transactionData) {
        return await this.request(`transactions.php/${id}`, {
            method: 'PUT',
            body: JSON.stringify(transactionData)
        });
    }

    async deleteTransaction(id) {
        return await this.request(`transactions.php/${id}`, {
            method: 'DELETE'
        });
    }

    async searchTransactions(searchTerm) {
        return await this.request(`transactions.php?search=${encodeURIComponent(searchTerm)}`);
    }

    async getTransactionsByDateRange(startDate, endDate) {
        return await this.request(`transactions.php?start_date=${startDate}&end_date=${endDate}`);
    }

    // Utility methods
    isAuthenticated() {
        return !!this.token;
    }

    getCurrentUser() {
        const userData = localStorage.getItem('user_data');
        return userData ? JSON.parse(userData) : null;
    }

    // Test API connection
    async testConnection() {
        try {
            await this.request('customers.php');
            return true;
        } catch (error) {
            console.error('API connection test failed:', error);
            return false;
        }
    }

    // Migration helper - sync local storage data to database
    async migrateLocalData() {
        try {
            // Get data from localStorage
            const customers = JSON.parse(localStorage.getItem('customers') || '[]');
            const transactions = JSON.parse(localStorage.getItem('transactions') || '[]');

            const results = {
                customers: { success: 0, failed: 0 },
                transactions: { success: 0, failed: 0 }
            };

            // Migrate customers
            for (const customer of customers) {
                try {
                    await this.createCustomer(customer);
                    results.customers.success++;
                } catch (error) {
                    console.error('Failed to migrate customer:', customer, error);
                    results.customers.failed++;
                }
            }

            // Migrate transactions
            for (const transaction of transactions) {
                try {
                    await this.createTransaction(transaction);
                    results.transactions.success++;
                } catch (error) {
                    console.error('Failed to migrate transaction:', transaction, error);
                    results.transactions.failed++;
                }
            }

            return results;
        } catch (error) {
            console.error('Migration failed:', error);
            throw error;
        }
    }
}

// Create and export a singleton instance
const apiService = new APIService();
export default apiService;

// Export individual methods for convenience
export const {
    login,
    logout,
    getCustomers,
    getCustomer,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    searchCustomers,
    getTransactions,
    getTransactionStats,
    createTransaction,
    updateTransaction,
    deleteTransaction,
    searchTransactions,
    getTransactionsByDateRange,
    isAuthenticated,
    getCurrentUser,
    testConnection,
    migrateLocalData
} = apiService;
